import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { ReplaySubject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import UserUtil, { formatUserName } from 'app/core/utilities/user-util';
import { SnackBarComponent, SnackBarData } from 'app/shared/snackbar/snackbar.component';
import { AppState, Timeout } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import User from 'entity/User';
import { BaseUser, InteractionMode } from 'messages/websocket.messages';

import { BadgingService } from '../services/badging/badging.service';
import { DeviceService } from '../services/device/device.service';
import { EmoteService, Emoticon, UserEmote } from '../services/emote/emote.service';
import { FavoriteService } from '../services/favorite.service';
import { CrossRoomParticipantService, CrossRoomType } from '../services/presentation/cross-room-participant.service';
import { PresentationService } from '../services/presentation/presentation.service';
import { SceneService } from '../services/scene.service';
import { VideoRoomService } from '../services/video-room.service';
import { socialAppActions, socialAppSelectors } from '../state/app';
import { HandposeDetectionService } from '../services/handpose-detection.service';

const UNDEFINED_STREAM = { id: uuidv4(), stream: undefined };

/**
 * Component responsible for displaying a given user's video and avatar when video is not available
 */
@Component({
  selector: 'app-video-bubble',
  templateUrl: './video-bubble.component.html',
  styleUrls: ['./video-bubble.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoBubbleComponent extends Loggable implements OnInit, OnDestroy, AfterViewInit {
  private videoRoomService = inject(VideoRoomService);
  private crossRoomParticipantService = inject(CrossRoomParticipantService);
  private deviceService = inject(DeviceService);
  private sceneService = inject(SceneService);
  private store = inject<Store<AppState>>(Store);
  private favoriteService = inject(FavoriteService);
  private _snackBar = inject(MatSnackBar);
  private presentationService = inject(PresentationService);
  private badgingService = inject(BadgingService);
  private emoteService = inject(EmoteService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private handPoseDetectionService = inject(HandposeDetectionService);

  public readonly mirror = input(false);
  public readonly size = input<number>();
  public readonly ringColor = input<string>();
  public readonly visualIndicators = input(false);
  public readonly hover = input(false);
  public readonly isGallery = input(false);
  public readonly isSpeaking = input(false);
  public readonly showAnimation = input<boolean | undefined>(false);
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  public get user(): User | undefined {
    return this._user;
  }
  public set user(value: User | undefined) {
    this._user = value;
    this.onUserChange();
  }

  private readonly videoRef = viewChild<ElementRef<HTMLVideoElement | undefined>>('videoElement');
  public videoEnabled = true;
  public audioEnabled = true;
  public videoLoading = true;
  public showMediaState = false;
  public nameTag?: string;
  private emoteTimeout?: Timeout;
  protected emote?: Emoticon = undefined;
  private emoteTimeoutDuration = 5000;

  public participants: User[] = [];

  private _user?: User;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private localUserId = '';
  private userVideoSubscription?: Subscription;
  protected isPresenting = false;
  protected isUserSpeakingInGallery = false;
  private badgeNames: Record<string, string> = {};
  protected isFavorite = false;

  public presentationV3 = false;

  public constructor() {
    super();
    this.setDisplayName('VideoBubbleComponent');
    this.badgingService.badgeNames$.pipe(takeUntil(this.destroyed$)).subscribe((badgeNames) => {
      this.badgeNames = badgeNames;
    });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_v3)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation_v3) => {
        this.presentationV3 = presentation_v3;
        this.changeDetectorRef.markForCheck();
      });
  }

  // #region Lifecycle Methods
  public ngOnInit(): void {
    this.store
      .select(socialAppSelectors.getPresenterIds)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presenterIds) => {
        this.isPresenting = presenterIds.includes(this._user?.userId ?? 'invalid');
        this.changeDetectorRef.markForCheck();
      });

    this.emoteService.emoteStream$.pipe(takeUntil(this.destroyed$)).subscribe((emote) => {
      if (emote.userId === this.user?.userId && emote.timestamp > Date.now() - this.emoteService.emooteLength) {
        this.updateEmote(emote);
        this.changeDetectorRef.markForCheck();
      }
    });

    if (this.isGallery()) {
      this.store
        .select(socialAppSelectors.getPresentation)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((presentation) => {
          if (!presentation?.speakingParticipants) {
            return;
          }

          const mode = presentation.settings?.interactionMode;
          const availableSpeakers = mode === InteractionMode.SINGLE_PUSH_TO_SPEAK ? 1 : 4;

          let speakers = Object.keys(presentation?.speakingParticipants ?? {});
          speakers = speakers.splice(0, availableSpeakers);
          const { userId } = this.user ?? {};

          if (userId && speakers.includes(userId)) {
            this.isUserSpeakingInGallery = true;
            this.changeDetectorRef.markForCheck();
            return;
          }

          if (this.isUserSpeakingInGallery && userId && !speakers.includes(userId)) {
            this.isUserSpeakingInGallery = false;
            this.changeDetectorRef.markForCheck();
            return;
          }
        });

      // If user is local, set speaking based on isUserSpeaking
      this.store
        .select(socialAppSelectors.isUserSpeaking)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((isUserSpeaking) => {
          if (this.localUserId === this.user?.userId) {
            this.isUserSpeakingInGallery = isUserSpeaking;
            this.changeDetectorRef.markForCheck();
          }
        });
    }

    // Subscribe after the component has been initialized with a user.
    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe(({ userId }) => {
        this.localUserId = userId ?? '';
        this.changeDetectorRef.markForCheck();
      });

    // Retrieve the participants video and audio state
    this.store
      .select(socialAppSelectors.getParticipantMediaState)
      .pipe(
        filter(() => Boolean(this._user)),
        map((participantsState) => {
          return participantsState[this._user?.userId ?? ''];
        }),
        filter(notNil),
        distinctUntilChanged((a, b) => a.audioMuted === b.audioMuted && a.videoMuted === b.videoMuted),
        tap(({ audioMuted, videoMuted }) => {
          this.audioEnabled = !audioMuted;
          this.videoEnabled = !videoMuted;
          this.changeDetectorRef.markForCheck();
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    this.favoriteService.favoriteListStream$.pipe(takeUntil(this.destroyed$)).subscribe((favorites) => {
      const faved = favorites.includes(this.user?.userId ?? '');

      if (faved && !this.isFavorite) {
        this.isFavorite = true;
        this.changeDetectorRef.markForCheck();
      } else if (!faved && this.isFavorite) {
        this.isFavorite = false;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private updateEmote(userEmote: UserEmote) {
    clearTimeout(this.emoteTimeout);
    if (!userEmote.emote) {
      return;
    }
    this.emote = userEmote.emote;
    this.changeDetectorRef.markForCheck();
    this.emoteTimeout = setTimeout(() => {
      this.emote = undefined;
      this.changeDetectorRef.markForCheck();
    }, this.emoteTimeoutDuration);
  }

  public ngAfterViewInit(): void {
    this.onUserChange();
    this.changeDetectorRef.markForCheck();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    const videoRef = this.videoRef();
    if (videoRef && videoRef.nativeElement) {
      videoRef.nativeElement.srcObject = null;
      videoRef.nativeElement = undefined;
    }
    this.removeListeners();
  }

  // #endregion Lifecycle Methods

  // #region Getters
  public get isUserInRoom(): boolean {
    if (!this.user) {
      return true;
    }
    assertDefined(this.user.userId);
    const participants = this.sceneService.sceneManager?.participantsAndPlayerList ?? [];
    const ids = participants.map((user) => user.userId);
    return ids.includes(this.user.userId);
  }

  /**
   * Determines if this is the local user, or they are remote
   */
  public get isLocal(): boolean {
    return this.localUserId === this._user?.userId;
  }

  private isBaseUser(obj: CrossRoomType): obj is BaseUser {
    return 'cameraOn' in obj && 'micOn' in obj;
  }

  /**
   *  Produce a user-unique video element id
   */
  public get videoElementId(): string {
    assertDefined(this.user?.userId);
    const { firstName, userId } = this.user;
    return `videoelement_${userId}_${firstName}`;
  }

  public get ringBorder(): string {
    const ringColor = this.ringColor();
    if (ringColor) {
      return `solid 5px ${ringColor}`;
    } else {
      return this.presentationV3 ? '' : 'solid 3px #777777';
    }
  }

  // #endregion Getters

  // #region Effects
  private removeListeners(): void {
    this.userVideoSubscription?.unsubscribe();
    this.userVideoSubscription = undefined;
  }

  /**
   * Handle an update to the current users media stream, ensuring that it is applied to the video element source
   * @param param0 {id: string, stream: MediaStream}
   * @param param0.id The id of the stream
   * @param param0.stream The users video stream, or undefined if not available
   * @returns
   */
  private handleMediaStreamChange({ id, stream }: { stream?: MediaStream; id: string }): void {
    const videoElement = this.videoRef()?.nativeElement;
    if (!videoElement) {
      this.trace('handleMediaStreamChange: Failed to get video element');
      return;
    }

    // Avoid resetting the video source if this media stream has already been set. This avoids video flicker when a
    // different user joins or mutes their video
    if (videoElement.getAttribute('data-stream-id') === id) {
      this.trace('handleMediaStreamChange: skipping video stream update, stream already set as source');
      return;
    }

    this.videoLoading = true;
    if (!stream?.active) {
      this.trace('handleMediaStreamChange: stream not active');
      return;
    }

    videoElement.srcObject = stream ?? null;
    videoElement.setAttribute('data-stream-id', id);

    if (!videoElement.oncanplay) {
      videoElement.oncanplay = async () => {
        videoElement.oncanplay = null;
        try {
          await videoElement.play();
          this.videoLoading = false;

          if (this.isLocal) {
            await this.handPoseDetectionService.initHandpose(videoElement);
          }

          this.changeDetectorRef.markForCheck();
        } catch (e) {
          this.error('handleMediaStreamChange: Failed to play video', e);
        }
      };
    } else {
      this.videoLoading = false;
    }
  }

  private onUserChange(): void {
    if (!this.user) {
      return;
    }
    assertDefined(this.user.userId);

    this.removeListeners();
    const { userId } = this.user;
    const isLocal = this.user.userId === this.localUserId;

    // If user is in gallery and cross participant
    // Set audio and video based on micOn and cameraOn
    const isGallery = this.isGallery();
    if (this.isBaseUser(this.user) && isGallery && !this.isUserInRoom) {
      this.audioEnabled = this.user?.micOn ?? false;
      this.videoEnabled = this.user?.cameraOn ?? false;
    }

    // Allow badge override
    if (this.badgeNames[userId] && this.badgeNames[userId].trim().length) {
      this.nameTag = this.badgeNames[userId].trim();
    } else {
      this.nameTag = this.user ? UserUtil.trimName(15, formatUserName(this.user)) : '';
    }

    if (isLocal) {
      this.userVideoSubscription = this.deviceService.cameraStream$
        ?.pipe(
          map((stream) => (stream ? { stream, id: stream.id } : UNDEFINED_STREAM)),
          takeUntil(this.destroyed$),
        )
        .subscribe(({ id, stream }) => {
          this.handleMediaStreamChange({ id, stream });
        });
    } else if (!this.isUserInRoom) {
      /*
        It seems that this subscription was based in the old gallery,
        and after the updates it is deprecated and resulting in bugs.
        Using the same source as the gallery seems to resolve the bug.
        TO-DO: fix and improve the logic related to mirrorMediaStream
      */
      // Speaking Participants
      // if (this.isSpeaking) {
      //   const mirrorId = createMirrorId({ userId }, MirrorType.PUBLISHER);
      //   this.userVideoSubscription = this.presentationSpeakerService.mirrorMediaStreams$
      //     .pipe(
      //       map((streams: MirrorMediaStreams) => {
      //         const { video: stream, videoId: id } = streams[mirrorId] ?? {
      //           videoId: UNDEFINED_STREAM.id,
      //           stream: UNDEFINED_STREAM.stream,
      //         };
      //         return { id, stream };
      //       }),
      //       takeUntil(this.destroyed$),
      //     )
      //     .subscribe(({ id, stream }) => this.handleMediaStreamChange({ id, stream: stream as MediaStream }));
      // }

      // Gallery Participants
      if (isGallery || this.isSpeaking()) {
        this.userVideoSubscription = this.crossRoomParticipantService.crossRoomParticipantVideoStream$
          .pipe(
            map((streams) => streams[userId] ?? UNDEFINED_STREAM),
            takeUntil(this.destroyed$),
          )
          .subscribe((stream) => {
            this.handleMediaStreamChange({ stream: stream as MediaStream, id: userId });
            this.changeDetectorRef.markForCheck();
          });
      }
    } else {
      // Default Participants inRoom
      this.userVideoSubscription = this.videoRoomService.participantVideoStream$
        .pipe(
          filter((streams) => streams[userId] !== undefined && !!streams[userId] && !!streams[userId].stream?.active),
          map((streams) => streams[userId] ?? UNDEFINED_STREAM),
          takeUntil(this.destroyed$),
        )
        .subscribe(({ id, stream }) => {
          this.handleMediaStreamChange({ stream, id });
        });
    }
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Handle hiding and showing media elements when in Presentation V1
   */
  public onMouseEnter(element: HTMLElement) {
    if (element) {
      this.showMediaState = true;
    }
  }

  public onMouseOut(element: HTMLElement) {
    if (element) {
      this.showMediaState = false;
    }
  }

  protected addOrRemoveFavorites() {
    if (this.isFavorite) {
      this.favoriteService.unfavoriteParticipant(this.localUserId, this.user?.userId);
    } else {
      this.favoriteService.favoriteParticipant(this.localUserId, this.user?.userId);
    }
    this.openFavoriteNotification(!this.isFavorite);
    this.changeDetectorRef.markForCheck();
  }

  private openFavoriteNotification(favorited: boolean, duration = 2000): void {
    const _data: SnackBarData = {
      message: `${this.user?.firstName ?? ''} has been ${favorited ? 'added to' : 'removed from'} your favorites`,
      type: 'success',
      icon: 'FAVORITE_STAR_FILL_SUCCESS',
      mode: this.presentationService.isPresentationActive ? 'dark' : 'light',
    };
    this._snackBar.openFromComponent(SnackBarComponent, {
      duration: duration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'custom-snackbar',
      data: _data,
    });
  }

  public openBadge(): void {
    const { userId } = this.user ?? {};
    const open = true;
    this.store.dispatch(
      socialAppActions.TOGGLE_BADGE_OPEN({ open, userId: userId ? userId : '', ringColor: this.ringColor() }),
    );
  }
  // #endregion Effects
}
